import { HttpService } from 'core/modules/network-service';
import { get, defaultTo, head } from 'lodash';

// local dependencies
import { SERVICE_RESPONSE_STATUS } from 'constants/common';
import { getVisibleItems } from 'resolvers/getVisibleEntities';

/*************************************************************/
/**
 * @type {class}
 * @desc MemberSecuritySettingsInfo defines an HTTP service interface to send HTTP network requets.
 * @example
 * import { MemberSecuritySettingsInfoService } from 'services/memberSecuritySettingsInfo';
 *
 * // create service instance and send/cancel request
 * const memberSecuritySettingsInfoService = new MemberSecuritySettingsInfoService();
 * const result = await memberSecuritySettingsInfoService.fetchData( '<user-id>' ); // fetch data
 * memberSecuritySettingsInfoService.cancel(); // cancel an active request
 */
export class MemberSecuritySettingsInfoService extends HttpService {

    // make request and fetch data
    async fetchData( { url = {}, data = {} } = {} ) {

        try {
            const response = await this.post( {
                host: CONFIG.host,
                path: url,
                data,
            } );

            // extract component data
            const item = get( response, 'body.component.structure.securityInfo', {} );
            const visibility = get( response, 'body.component.visibility', {} );
            const actions = get( response, 'body.component.structure.actions', {} );

            if( SERVICE_RESPONSE_STATUS.SUCCESS === get( response, 'body.status', SERVICE_RESPONSE_STATUS.FAILURE ) ) {
                return {
                    status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.FAILURE ),
                    data: {
                        actions: getVisibleItems( actions, visibility ),
                        securityInfo: {
                            title: null,
                            values: get( response, 'body.component.data.state.securityInfo', {} ),
                            ...item
                        },
                        visibility
                    },
                };
            } else {
                
                return {
                    status: SERVICE_RESPONSE_STATUS.FAILURE,
                    data: get( response, 'body.component.data', {} )
                };
            }
            
        } catch( errResponse ) {
            return this._errorFormatter( errResponse );
        }
    }


    async fetchAddEditForm( { url = {}, data = {} } = {} ) {
        
        try {
            const response = await this.post( {
                host: CONFIG.host,
                path: url,
                data
            } );

            const fields = get( response, 'body.component.structure.fields', {} );
            const actions = get( response, 'body.component.structure.actions', [] );
            const values = get( response, 'body.component.data', [] );
            const addTitle = get( response, 'body.component.structure.title', [] );
            
            return {
                status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.FAILURE ),
                data: {
                    fields,
                    actions,
                    values,
                    addTitle
                },
            };
        } catch( errResponse ) {
            return this._errorFormatter( errResponse );
        }
    }

    async updateEmail( { url = {}, data = {} } = {} ) {

        const { id = null, email} = data.data;

        try {
            const response = await this.post( {
                host: CONFIG.host,
                path: url,
                data: data
            } );
            
            return {
                _status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.SUCCESS ),
                _data: {
                    id: defaultTo( head( get( response, 'body.component.data.itemIds', [] ) ), id ),
                    email
                },
            };
        } catch( error ) {
            return {
                status: SERVICE_RESPONSE_STATUS.FAILURE,
                data: null,
            };
        }
    }

    async resetPassword( { url = {}, data = {} } = {} ) {
        
        const { id = null, email } = data.data;

        try {
            const response = await this.post( {
                host: CONFIG.host,
                path: url,
                data: data
            } );
            
            return {
                _status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.SUCCESS ),
                _data: {
                    id: defaultTo( head( get( response, 'body.component.data.itemIds', [] ) ), id ),
                    email
                },
            };
        } catch( error ) {
            return {
                status: SERVICE_RESPONSE_STATUS.FAILURE,
                data: null,
            };
        }

    }

    // format error response
    _errorFormatter( errResponse ) {
        return errResponse; // some logic
    }
}
